import { TableActionIconButton } from "_shared/_views/tables/table-toolbar";
import { TableActionButton } from "_shared/_views/tables/table-toolbar";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import DomainAddIcon from "@mui/icons-material/DomainAdd";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ExitIcon from "@mui/icons-material/TransitEnterexit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import Box from "@mui/material/Box";

import Chip from "@mui/material/Chip";
import { styled } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CondensedCell from "_shared/_views/tables/condensed-cell";
import RowActions, { type RowAction } from "_shared/_views/tables/row-actions";
import RowLink from "_shared/_views/tables/row-link";
import SearchMatch from "_shared/_views/tables/search-match";
import { MRTNoMemo } from "_shared/_views/tables/table";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import Guid from "_shared/guids/guid";
import * as Config from "config";
import CompanyAvatar from "marketplace/_views/avatars/company-avatar";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import TeamAvatar from "marketplace/_views/avatars/team-avatar";
import { PageTab } from "marketplace/_views/profiles/profiles-page-tabs";
import { ProfileTableData } from "marketplace/_views/profiles/profile-table-data";
import VisibilityToggleSwitch from "marketplace/_views/profiles/visibility-toggle-switch";
import Company from "marketplace/companies/company";
import Individual from "marketplace/individuals/individual";
import { publishingBlockedReason } from "marketplace/individuals/profiles/professional-credentials";
import Team from "marketplace/teams/team";
import {
  createMRTColumnHelper,
  MRT_GlobalFilterTextField as GlobalFilterTextField,
  type MRT_TableInstance,
  type MRT_TableOptions,
} from "material-react-table";
import {
  MobileToolbarButtonsContainer,
  TableTitleRow,
  ScrollableTabs,
  searchFieldSpacing,
  searchTextFieldProps,
  topToolbarFitsThePage,
  ToolbarButtons,
} from "_shared/_views/tables/table-toolbar";
import { JSX, useCallback, useMemo } from "react";
import { useNavigate } from "react-router";
import { NoValueDash } from "_shared/_views/tables/no-value-dash";
import { TableToolsMenu } from "_shared/_views/tables/table-tools-menu";

const ToolbarButtonsContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  display: "flex",
  alignItems: "end",
  flexDirection: "column",
  gap: theme.spacing(1),
}));
const TableTopToolbarContainer = styled("div")(() => ({
  maxWidth: "100%",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  width: "100%",
}));
const LowerTopToolbarContainer = styled("div")(({ theme }) => ({
  minWidth: 0,
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
  display: "flex",
  flexDirection: "row",
  alignItems: "left",
  width: "100%",
}));
const TableTitle = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
}));
const NewProfileButtonContainer = styled("span")<{ disabled: boolean }>(({ disabled }) => ({
  cursor: disabled ? "not-allowed" : "default",
}));
const NameCellContainer = styled(Box)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(1.5),
  minWidth: 0,
}));
const CategoryChip = styled(Chip)(({ theme }) => ({
  color: "#777",
  marginRight: theme.spacing(0.4),
}));

/**
 * Why this row cannot be shown on the Marketplace yet, or undefined. Only a hidden individual is
 * held back: a profile is published once it lists professional credentials (#6608). A published one
 * can always be hidden, and companies and teams are not held to it.
 */
function publishBlockedReasonFor(profile: ProfileTableData): string | undefined {
  if (!(profile instanceof Individual) || profile.isVisible) return undefined;
  return publishingBlockedReason(profile.profile?.credentials);
}

/** The tab a profile is listed on, which is also the first segment of its route. */
function profileTab(profile: ProfileTableData): PageTab {
  if (profile instanceof Company) return PageTab.Company;
  if (profile instanceof Team) return PageTab.Teams;
  return PageTab.Individual;
}

/**
 * Where a profile opens: the row, the name and the ⋯'s Open all go here (#6722). A profile the user
 * cannot edit opens read-only, so nobody is handed an editor whose save the API would refuse.
 */
export function profileDestination(profile: ProfileTableData, readOnly: boolean): string {
  return `/marketplace/profiles/${profileTab(profile)}/${readOnly ? "view" : "edit"}/${profile.id}`;
}

/** The line under a profile's name: the organization an individual's profile belongs to. */
function profileOrganization(profile: ProfileTableData): string | undefined {
  if (!(profile instanceof Individual)) return undefined;
  return profile.company?.profile?.name;
}

export const NAME_COLUMN = "name";
export const CATEGORIES_COLUMN = "categories";
export const VISIBILITY_COLUMN = "isVisible";

const columnHelper = createMRTColumnHelper<ProfileTableData>();
type ProfilesTable = MRT_TableInstance<ProfileTableData>;

type MarketplaceProfilesTableProps = {
  activeTab?: PageTab;
  addProfileDisabled: boolean;
  addProfileDisabledMessage: string;
  canViewTeamProfiles: boolean;
  currentUserId?: Guid;
  getShouldDisableRowEditing: (profile: ProfileTableData) => boolean;
  isError: boolean;
  isLoading: boolean;
  isMediumDisplaySize: boolean;
  onCreateProfile: () => void;
  onDeleteProfile: (profile: ProfileTableData) => void;
  onLeaveTeam: (profile: ProfileTableData) => void;
  onPublishProfile: (profile: ProfileTableData) => void;
  onRetry: () => void;
  onTabChange: (tab: PageTab) => void;
  profiles: ProfileTableData[];
  showSkeletons: boolean;
  viewingAsVendor?: boolean;
};

const newProfileIcon = (activeTab: PageTab) => {
  if (activeTab === PageTab.Individual) return <PersonAddIcon fontSize="large" />;
  if (activeTab === PageTab.Company) return <DomainAddIcon fontSize="large" />;
  if (activeTab === PageTab.Teams) return <GroupAddIcon fontSize="large" />;
};

function profileName(profile: ProfileTableData): string {
  return profile instanceof Individual
    ? `${profile.profile?.firstName} ${profile.profile?.lastName}`
    : (profile.profile?.name ?? "");
}

export default function MarketplaceProfilesTable(props: Readonly<MarketplaceProfilesTableProps>) {
  const {
    activeTab,
    addProfileDisabled,
    addProfileDisabledMessage,
    canViewTeamProfiles,
    currentUserId,
    getShouldDisableRowEditing,
    isError,
    isLoading,
    isMediumDisplaySize,
    onCreateProfile,
    onDeleteProfile,
    onLeaveTeam,
    onPublishProfile,
    onRetry,
    onTabChange,
    profiles,
    showSkeletons,
    viewingAsVendor,
  } = props;

  const navigate = useNavigate();

  const destinationOf = useCallback(
    (profile: ProfileTableData) => profileDestination(profile, getShouldDisableRowEditing(profile)),
    [getShouldDisableRowEditing],
  );

  // The browser searches every column's value, so each cell marks the search in the text it draws (#6590).
  const renderNameColumn = useCallback(
    (profileData: ProfileTableData, table: ProfilesTable): JSX.Element => {
      const organization = profileOrganization(profileData);
      return (
        <NameCellContainer>
          {profileData instanceof Individual && <IndividualAvatar individual={profileData ?? null} />}
          {profileData instanceof Company && <CompanyAvatar company={profileData} />}
          {profileData instanceof Team && <TeamAvatar team={profileData} />}
          <CondensedCell
            title={
              <RowLink to={destinationOf(profileData)}>
                <SearchMatch table={table} text={profileName(profileData)} />
              </RowLink>
            }
            secondary={organization && <SearchMatch table={table} text={organization} />}
          />
        </NameCellContainer>
      );
    },
    [destinationOf],
  );

  const renderCategoriesColumn = useCallback((profileData: ProfileTableData, table: ProfilesTable): JSX.Element => {
    if (!profileData.profile?.categories || profileData.profile.categories.length < 1) {
      return <NoValueDash />;
    }
    return (
      <div>
        {profileData.profile?.categories?.map((label: string) => (
          <CategoryChip key={label} label={<SearchMatch table={table} text={label} />} size="small" />
        ))}
      </div>
    );
  }, []);

  const renderVisibilityColumn = useCallback(
    (profile: ProfileTableData, table: ProfilesTable): JSX.Element => {
      // Someone who cannot edit the profile cannot change whether it is searchable either, so they
      // are told the answer rather than shown a switch they can never move.
      if (getShouldDisableRowEditing(profile)) {
        return (
          <Typography variant="body2">
            <SearchMatch table={table} text={profile.isVisible ? "Yes" : "No"} />
          </Typography>
        );
      }
      const blockedReason = publishBlockedReasonFor(profile);
      return (
        <Tooltip
          title={
            blockedReason ??
            (profile.isVisible ? "Profile is searchable on Marketplace" : "Profile is hidden from Marketplace")
          }
        >
          <span>
            <VisibilityToggleSwitch
              checked={profile.isVisible}
              disabled={Boolean(blockedReason)}
              ariaLabel={profile.isVisible ? "Hide profile from Marketplace" : "Show profile on Marketplace"}
              stopPropagation
              onChange={() => onPublishProfile(profile)}
            />
          </span>
        </Tooltip>
      );
    },
    [getShouldDisableRowEditing, onPublishProfile],
  );

  // Every action is `inMenu`: the table drew one ⋯ at every width before it used the shared component,
  // and it still does, now with Open first. Opening the profile is Open, so there is no Edit item.
  const rowActionsFor = useCallback(
    (profile: ProfileTableData): RowAction[] => {
      const actions: RowAction[] = [];
      const name = profileName(profile);
      if (viewingAsVendor && !getShouldDisableRowEditing(profile)) {
        const blockedReason = publishBlockedReasonFor(profile);
        actions.push({
          key: "publish",
          label: profile.isVisible ? "Hide From Marketplace" : "Show On Marketplace",
          icon: profile.isVisible ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />,
          inMenu: true,
          disabled: Boolean(blockedReason),
          disabledReason: blockedReason,
          onClick: () => onPublishProfile(profile),
        });
      }

      if (activeTab !== PageTab.Teams || !(profile instanceof Team)) return actions;

      if (profile.leader?.userId?.isEqualTo(currentUserId)) {
        actions.push({
          key: "delete",
          label: "Delete",
          ariaLabel: `Delete ${name}`,
          icon: <DeleteIcon fontSize="small" />,
          destructive: true,
          inMenu: true,
          onClick: () => onDeleteProfile(profile),
        });
        return actions;
      }

      actions.push({
        key: "leave",
        label: "Leave Team",
        ariaLabel: `Leave ${name}`,
        icon: <ExitIcon fontSize="small" />,
        inMenu: true,
        onClick: () => onLeaveTeam(profile),
      });
      return actions;
    },
    [activeTab, currentUserId, getShouldDisableRowEditing, onDeleteProfile, onLeaveTeam, onPublishProfile, viewingAsVendor],
  );

  // Each column's value is the text the browser sorts, searches and filters on; the cell draws it.
  // An accessor that returned the JSX itself left the table nothing to compare.
  const columns = useMemo(
    () => [
      columnHelper.accessor(profileName, {
        header: "Name",
        id: NAME_COLUMN,
        meta: { kind: "name" },
        Cell: ({ row, table }) => renderNameColumn(row.original, table),
      }),
      columnHelper.accessor((profile) => profile.profile?.categories?.join(", ") ?? "", {
        header: "Categories",
        id: CATEGORIES_COLUMN,
        meta: { kind: "tags" },
        Cell: ({ row, table }) => renderCategoriesColumn(row.original, table),
      }),
      columnHelper.accessor((profile) => (profile.isVisible ? "Yes" : "No"), {
        header: "Searchable on Marketplace",
        id: VISIBILITY_COLUMN,
        filterVariant: "select",
        filterSelectOptions: ["Yes", "No"],
        // A yes or no, drawn as the switch that changes it: the short, fixed-width value a status is.
        meta: { kind: "status" },
        Cell: ({ row, table }) => renderVisibilityColumn(row.original, table),
      }),
    ],
    [renderCategoriesColumn, renderNameColumn, renderVisibilityColumn],
  );

  const tableOptions: MRT_TableOptions<ProfileTableData> = {
    columns,
    data: profiles,
    enableRowSelection: false,
    enableTableHead: true,
    // Deleting a team and leaving one live nowhere else in the UI — `deleteProfile` and `leaveTeam`
    // are wired up in `profile-type-page` and reach the user through this menu alone. Turning row
    // actions off left both unreachable while the column stayed pinned below, which is what gave the
    // "built, disabled, never removed" look.
    enableRowActions: true,
    enableColumnPinning: true,
    initialState: {
      showGlobalFilter: true,
      columnPinning: { right: ["mrt-row-actions"] },
    },
    positionGlobalFilter: "none",
    muiSearchTextFieldProps: ({ table }) =>
      searchTextFieldProps({ searches: "profiles", table }),
    renderEmptyRowsFallback: emptyRowsFallback({
      isError,
      isLoading,
      onRetry,
      subject: "profiles",
    }),
    renderToolbarInternalActions: ({ table }) => (
      <>
        {!isMediumDisplaySize && (
          <ToolbarButtonsContainer>
            <ToolbarButtons>
              <GlobalFilterTextField table={table} sx={searchFieldSpacing} />
              <TableToolsMenu table={table} />
            </ToolbarButtons>
            <Tooltip title={addProfileDisabledMessage}>
              <span>
                <NewProfileButtonContainer disabled={addProfileDisabled}>
                  <TableActionButton
                    color="primary"
                    disabled={addProfileDisabled}
                    startIcon={activeTab ? newProfileIcon(activeTab) : null}
                    onClick={onCreateProfile}
                  >
                    {Config.defaultProfileName}
                  </TableActionButton>
                </NewProfileButtonContainer>
              </span>
            </Tooltip>
          </ToolbarButtonsContainer>
        )}
      </>
    ),
    renderTopToolbarCustomActions: ({ table }) => {
      if (!activeTab) return null;
      return (
        <TableTopToolbarContainer>
          <TableTitleRow>
            <TableTitle variant="h4">Marketplace Profiles</TableTitle>
            {isMediumDisplaySize && (
              <Tooltip title={addProfileDisabledMessage}>
                <span>
                  <NewProfileButtonContainer disabled={addProfileDisabled}>
                    <TableActionIconButton color="primary" disabled={addProfileDisabled} onClick={onCreateProfile}>
                      {newProfileIcon(activeTab)}
                    </TableActionIconButton>
                  </NewProfileButtonContainer>
                </span>
              </Tooltip>
            )}
          </TableTitleRow>
          <LowerTopToolbarContainer>
            <ScrollableTabs
              value={activeTab}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              onChange={(_event, newValue: PageTab) => onTabChange(newValue)}
            >
              <Tab value={PageTab.Individual} label="Individual Profile" />
              <Tab value={PageTab.Company} label="Company Profile" />
              {canViewTeamProfiles && <Tab value={PageTab.Teams} label="Team Profiles" />}
            </ScrollableTabs>
          </LowerTopToolbarContainer>
          {isMediumDisplaySize && (
            <MobileToolbarButtonsContainer>
              <GlobalFilterTextField table={table} />
              <ToolbarButtons>
                <TableToolsMenu table={table} />
              </ToolbarButtons>
            </MobileToolbarButtonsContainer>
          )}
        </TableTopToolbarContainer>
      );
    },
    renderRowActions: ({ row }) => (
      <RowActions
        ariaLabel={`Actions for ${profileName(row.original)}`}
        open={{ to: destinationOf(row.original) }}
        actions={rowActionsFor(row.original)}
      />
    ),
    getRowId: (row) => row.id?.value ?? Guid.generate().value,
    // A user has about one profile of each kind per organisation they belong to, and every one of
    // them arrives in a single response, so the browser pages, searches, sorts and filters them.
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableColumnFilters: true,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => navigate(destinationOf(row.original)),
      sx: { cursor: "pointer" },
    }),
    muiTableContainerProps: ({ table }) => ({
      sx: {
        height: `calc(100% - ${table.refs.topToolbarRef.current?.offsetHeight}px - ${table.refs.bottomToolbarRef.current?.offsetHeight}px)`,
      },
    }),
    muiTopToolbarProps: { sx: topToolbarFitsThePage },
    muiTablePaperProps: {
      sx: {
        height: "100%",
      },
    },
    muiToolbarAlertBannerProps: isError
      ? {
          color: "error",
          children: "Error loading data",
        }
      : undefined,
    state: {
      isLoading,
      showAlertBanner: isError,
      showProgressBars: isLoading,
      showSkeletons,
    },
  };

  return <MRTNoMemo<ProfileTableData> options={tableOptions} persistColumnVisibilityAs="marketplace-profiles" cards />;
}
